import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  PRODUCTION_FORMAT_LABELS,
  ProductionFormat,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError, awaitingPaidReading } from '../lib/api.js';
import { useScriptRun } from '../lib/useScriptRun.js';
import { Callout } from '../components/Callout.js';
import { ScriptPrivacyNotice } from '../components/ScriptPrivacyNotice.js';
import { AiCostApproval } from '../components/credits/AiCostApproval.js';
import { NeedsReadingPanel } from '../components/credits/NeedsReadingPanel.js';
import { refusalCode } from '../components/credits/useProjectCredits.js';
import { readingState } from './StartProductionPage.js';

type Mode = 'UPLOAD' | 'PASTE' | 'MANUAL';
type Target = 'EXISTING' | 'NEW';

/**
 * What starting led to. `open` is today's flow (the run was requested, or
 * there is nothing to run); `approve` is a metered production, where the
 * screenplay is in and the cost must be approved before anything is read;
 * `paused` is metered AI switched off, where nothing can be read.
 */
type Started =
  | { kind: 'open'; projectId: string; tab: string }
  | { kind: 'approve'; projectId: string; scriptVersionId: string }
  | { kind: 'paused'; projectId: string };

/**
 * WHERE A LOCATION SEARCH STARTS.
 *
 * Until this page existed the only way in was to create a project, open it,
 * find the Script & Scenes tab and upload there — four steps before the product
 * did anything, and the one a producer arrives wanting to do was the last of
 * them. This is the same three verbs the workspace already has, in the order
 * somebody actually has them in mind: here is my material, put it in this
 * production, go.
 *
 * A SCRIPT IS NOT REQUIRED. "Add requirements manually" is a first-class third
 * mode, not a fallback — a commercial or a music video routinely has a brief
 * and a shot list and no screenplay at all, and a page that assumes a
 * screenplay turns them away at the door.
 *
 * NOTHING IS PROMISED THAT IS NOT TRUE. The notice below says what this service
 * actually does with a screenplay, which is not the same as what would be
 * reassuring to say — see the comment on `ScriptPrivacyNotice`.
 */
export function NewSearchPage({
  onOpen,
  onBack,
}: {
  onOpen: (projectId: string, tabSlug: string) => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<Mode>('UPLOAD');
  const [file, setFile] = useState<File | null>(null);
  const [pasted, setPasted] = useState('');
  const [target, setTarget] = useState<Target>('EXISTING');
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newFormat, setNewFormat] = useState<ProductionFormat>('FEATURE_FILM');
  const [error, setError] = useState<string | null>(null);
  const [landed, setLanded] = useState<Exclude<Started, { kind: 'open' }> | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.listProjects() });

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const all = projects.data ?? [];
    return term === '' ? all : all.filter((p) => p.title.toLowerCase().includes(term));
  }, [projects.data, filter]);

  const start = useMutation({
    mutationFn: async (): Promise<Started> => {
      // ONE PROJECT, WHICHEVER WAY IN. Creating it first means every later step
      // has somewhere to belong, and a failed upload leaves a real, openable
      // production rather than an orphaned file.
      const projectId =
        target === 'NEW'
          ? (
              await api.createProject({
                title: newTitle.trim(),
                format: newFormat,
                productionModel: 'TRADITIONAL',
              })
            ).id
          : (selected ?? '');

      if (projectId === '') throw new ApiError(400, null, 'Pick a project first');

      if (mode === 'MANUAL') return { kind: 'open', projectId, tab: 'locations' };

      const version =
        mode === 'UPLOAD' && file !== null
          ? await api.uploadScript(projectId, file)
          : await api.pasteScript(projectId, pasted);

      /**
       * NOTHING PAID RUNS BY ITSELF ONCE AI IS METERED (S6: "NewSearchPage no
       * longer runs AI by itself"). The wallet says how the production stands;
       * a wallet that cannot be read is today's flow, and the server refuses
       * that flow's verb on a metered production — which lands here too.
       */
      const billing = await api.credits(projectId).then(
        (wallet) => wallet.aiBilling,
        () => null,
      );
      if (billing === 'METERED') return { kind: 'approve', projectId, scriptVersionId: version.scriptVersionId };
      if (billing === 'SUSPENDED') return { kind: 'paused', projectId };

      // The analysis itself. It is a background job — the pipeline run id comes
      // back immediately and the Script tab shows the run's own progress.
      try {
        await api.requestExtraction(projectId, version.scriptVersionId);
      } catch (e: unknown) {
        if (refusalCode(e) === 'BILLING_APPROVAL_REQUIRED') {
          return { kind: 'approve', projectId, scriptVersionId: version.scriptVersionId };
        }
        if (refusalCode(e) === 'AI_BILLING_UNAVAILABLE') return { kind: 'paused', projectId };
        throw e;
      }

      return { kind: 'open', projectId, tab: 'script' };
    },
    onSuccess: (started) => {
      if (started.kind === 'open') {
        onOpen(started.projectId, started.tab);
        return;
      }
      setLanded(started);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'That could not be started.');
    },
  });

  if (landed !== null) {
    return (
      <div
        data-testid="new-search-page"
        style={{ maxWidth: 880, margin: '0 auto', padding: 'clamp(22px, 3vw, 34px) clamp(18px, 4vw, 40px) 64px' }}
      >
        <nav className="mm" style={{ fontSize: 12.5, marginBottom: 18 }} aria-label="Breadcrumb">
          <button
            type="button"
            data-testid="new-search-back"
            onClick={onBack}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
          >
            Projects
          </button>
          <span aria-hidden="true"> › </span>
          <span style={{ fontWeight: 700, color: 'var(--ink)' }}>New location search</span>
        </nav>

        <h1 className="dh" style={{ fontSize: 'clamp(26px, 4vw, 38px)', margin: 0 }}>
          Your screenplay is in
        </h1>

        {landed.kind === 'paused' ? (
          <div style={{ marginTop: 18 }}>
            <Callout tone="warn" title="AI analysis is paused" testId="new-search-paused">
              The screenplay is saved on the production and its scenes are found for free. The AI
              analysis cannot be started right now; anything already running finishes as usual.
            </Callout>
          </div>
        ) : (
          <NewSearchApproval
            projectId={landed.projectId}
            scriptVersionId={landed.scriptVersionId}
            onStarted={() => {
              onOpen(landed.projectId, 'script');
            }}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button
            type="button"
            className="ctl"
            data-testid="new-search-open-production"
            onClick={() => {
              onOpen(landed.projectId, 'script');
            }}
          >
            {landed.kind === 'paused' ? 'Open the production' : 'Open the production without analysing'}
          </button>
        </div>
      </div>
    );
  }

  const ready =
    (target === 'EXISTING' ? selected !== null : newTitle.trim() !== '') &&
    (mode === 'MANUAL' ||
      (mode === 'UPLOAD' ? file !== null : pasted.trim().length > 0));

  return (
    <div
      data-testid="new-search-page"
      style={{
        // THE PAGE'S OWN GUTTER. Every other screen carries one — the project
        // list, the workspace shell — because this app has no outer container;
        // without it the content sat flush against the left edge of the window,
        // which reads as a broken page rather than a missing rule. `clamp` so a
        // phone gets 18px instead of a desktop's 40 and the form is not a
        // column down the middle of a narrow screen.
        maxWidth: 1360,
        margin: '0 auto',
        padding: 'clamp(22px, 3vw, 34px) clamp(18px, 4vw, 40px) 64px',
      }}
    >
      <nav className="mm" style={{ fontSize: 12.5, marginBottom: 18 }} aria-label="Breadcrumb">
        <button
          type="button"
          data-testid="new-search-back"
          onClick={onBack}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
        >
          Projects
        </button>
        <span aria-hidden="true"> › </span>
        <span style={{ fontWeight: 700, color: 'var(--ink)' }}>New location search</span>
      </nav>

      <p
        className="flbl"
        style={{ color: 'var(--color-accent)', letterSpacing: '0.1em', margin: 0 }}
      >
        AI script breakdown
      </p>
      <h1 className="dh" style={{ fontSize: 'clamp(28px, 5vw, 46px)', margin: '10px 0 0' }}>
        Find locations for your production
      </h1>
      <p className="mm" style={{ fontSize: 14, margin: '12px 0 0', maxWidth: '62ch', lineHeight: 1.6 }}>
        Upload a screenplay, paste a creative brief, or describe what you need — we break it into
        scenes and location requirements, then match real locations.
      </p>

      <div className="split-2" style={{ gap: 24, marginTop: 26, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <div
            role="radiogroup"
            aria-label="How you want to start"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}
          >
            {MODES.map((m) => (
              <button
                key={m.mode}
                type="button"
                role="radio"
                aria-checked={mode === m.mode}
                data-testid={`search-mode-${m.mode}`}
                onClick={() => {
                  setMode(m.mode);
                }}
                style={{
                  textAlign: 'left',
                  padding: 16,
                  cursor: 'pointer',
                  background: 'var(--color-bg)',
                  border:
                    mode === m.mode
                      ? '2px solid var(--ink)'
                      : '1px solid var(--color-divider)',
                }}
              >
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{m.title}</span>
                <span className="mm" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
                  {m.hint}
                </span>
              </button>
            ))}
          </div>

          {mode === 'UPLOAD' ? (
            <FilePicker file={file} onPick={setFile} />
          ) : mode === 'PASTE' ? (
            <label style={{ display: 'block', marginTop: 16 }}>
              <span className="flbl">Screenplay, treatment or brief</span>
              <textarea
                data-testid="search-paste"
                value={pasted}
                rows={12}
                placeholder="Paste the pages, a treatment, or a shot list…"
                onChange={(e) => {
                  setPasted(e.target.value);
                }}
                style={{ width: '100%', marginTop: 6, padding: 12, fontSize: 13, border: '1px solid var(--color-divider)', fontFamily: 'inherit' }}
              />
            </label>
          ) : (
            <p
              className="mm"
              data-testid="search-manual-note"
              style={{ fontSize: 13, marginTop: 16, lineHeight: 1.6, maxWidth: '58ch' }}
            >
              We will open the production with an empty location list. Add each requirement — what
              the place has to be, where, and for which scenes — and matching runs against them the
              same way it would against a screenplay.
            </p>
          )}

          <ScriptPrivacyNotice />
        </div>

        <aside
          data-testid="search-target"
          style={{ border: '1px solid var(--color-divider)', padding: 18, minWidth: 0 }}
        >
          <p className="flbl" style={{ margin: 0, letterSpacing: '0.08em' }}>
            Save this search to
          </p>

          <label
            data-testid="target-existing"
            style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, padding: 12, cursor: 'pointer', border: target === 'EXISTING' ? '2px solid var(--ink)' : '1px solid var(--color-divider)' }}
          >
            <input
              type="radio"
              name="search-target"
              checked={target === 'EXISTING'}
              onChange={() => {
                setTarget('EXISTING');
              }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>Existing project</span>
          </label>

          {target === 'EXISTING' ? (
            <>
              <input
                type="search"
                data-testid="project-filter"
                value={filter}
                placeholder="Search projects"
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                style={{ width: '100%', height: 38, marginTop: 10, padding: '0 12px', fontSize: 13, border: '1px solid var(--color-divider)' }}
              />

              {projects.isPending ? (
                <p className="mm" style={{ fontSize: 12.5, marginTop: 12 }} data-testid="projects-loading">
                  Loading your productions…
                </p>
              ) : visible.length === 0 ? (
                <p className="mm" style={{ fontSize: 12.5, marginTop: 12 }} data-testid="projects-empty">
                  {(projects.data ?? []).length === 0
                    ? 'You have no productions yet — start a new one below.'
                    : 'No production matches that.'}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, maxHeight: 260, overflowY: 'auto' }}>
                  {visible.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        data-testid={`project-${p.id}`}
                        aria-pressed={selected === p.id}
                        onClick={() => {
                          setSelected(p.id);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background: selected === p.id ? 'var(--color-surface)' : 'transparent',
                          border: 0,
                          borderBottom: '1px solid var(--color-divider)',
                          display: 'flex',
                          gap: 10,
                          alignItems: 'baseline',
                        }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                            {p.title}
                          </span>
                          <span className="mm" style={{ fontSize: 11.5 }}>
                            {PRODUCTION_FORMAT_LABELS[p.format] ?? p.format} ·{' '}
                            {p.lifecyclePhase.toLowerCase().replace(/_/g, ' ')}
                          </span>
                        </span>
                        {selected === p.id ? (
                          <span aria-hidden="true" style={{ marginLeft: 'auto', fontWeight: 700 }}>
                            ✓
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}

          <label
            data-testid="target-new"
            style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, padding: 12, cursor: 'pointer', border: target === 'NEW' ? '2px solid var(--ink)' : '1px solid var(--color-divider)' }}
          >
            <input
              type="radio"
              name="search-target"
              checked={target === 'NEW'}
              onChange={() => {
                setTarget('NEW');
              }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>New project</span>
          </label>

          {target === 'NEW' ? (
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              <label>
                <span className="flbl">Title</span>
                <input
                  data-testid="new-project-title"
                  value={newTitle}
                  placeholder="Northern Light"
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                  }}
                  style={{ width: '100%', height: 38, marginTop: 6, padding: '0 12px', fontSize: 13, border: '1px solid var(--color-divider)' }}
                />
              </label>
              <label>
                <span className="flbl">Format</span>
                <select
                  data-testid="new-project-format"
                  value={newFormat}
                  onChange={(e) => {
                    setNewFormat(e.target.value as ProductionFormat);
                  }}
                  style={{ width: '100%', height: 38, marginTop: 6, padding: '0 10px', fontSize: 13, border: '1px solid var(--color-divider)', background: 'var(--color-bg)' }}
                >
                  {Object.entries(PRODUCTION_FORMAT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {error === null ? null : (
            <div style={{ marginTop: 14 }}>
              <Callout tone="danger" title="Not started" testId="new-search-error">
                {error}
              </Callout>
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            data-testid="analyse-locations"
            disabled={!ready || start.isPending}
            onClick={() => {
              setError(null);
              start.mutate();
            }}
            style={{ width: '100%', marginTop: 16 }}
          >
            {start.isPending ? 'Starting…' : mode === 'MANUAL' ? 'Open the production' : 'Analyse locations'}
          </button>

          {mode === 'MANUAL' ? null : (
            <p className="mm" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.6 }} data-testid="background-note">
              {/* What is TRUE: the run is a background job and the Script tab
                  shows its progress. The design said "we'll notify you when
                  it's ready" — this service sends no such notification, and a
                  promise of one is how somebody closes the tab and waits for an
                  email that never comes. */}
              Analysis runs in the background. You can leave this page — the production&rsquo;s
              Script tab shows how far it has got.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * THE COST OF THE ANALYSIS, ONCE THE SCREENPLAY HAS BEEN READ INTO SCENES.
 *
 * The estimate is sized from the scenes, so it waits for the free parse; a
 * scanned PDF stops at "Needs reading" with its own approval (P6), and a file
 * that could not be read into scenes says so. Nothing is read by AI until the
 * producer presses Start.
 */
function NewSearchApproval({
  projectId,
  scriptVersionId,
  onStarted,
}: {
  projectId: string;
  scriptVersionId: string;
  onStarted: () => void;
}) {
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.sceneCount > 0 || awaitingPaidReading(data) !== null)) return false;

      return 1500;
    },
  });
  const run = useScriptRun(scriptVersionId);

  const script = current.data?.scriptVersionId === scriptVersionId ? current.data : null;
  const paid = awaitingPaidReading(script);
  const reading = paid !== null ? null : readingState(script?.sceneCount ?? 0, run.progress);

  if (paid !== null) {
    return (
      <div style={{ marginTop: 18 }}>
        <NeedsReadingPanel
          projectId={projectId}
          scriptVersionId={scriptVersionId}
          pageCount={paid.pageCount}
          onStarted={onStarted}
        />
      </div>
    );
  }

  if (reading?.kind === 'failed') {
    return (
      <div style={{ marginTop: 18 }}>
        <Callout tone="warn" title="This file could not be read into scenes" testId="new-search-parse-failed">
          Nothing was analysed and nothing was charged. Open the production to try another file or to
          add the locations you need by hand.
        </Callout>
      </div>
    );
  }

  if (reading?.kind !== 'read') {
    return (
      <div style={{ marginTop: 18 }} data-testid="new-search-reading" aria-busy="true">
        <div className="sa-sweep-track" aria-hidden="true">
          <span className="sa-sweep-bar" />
        </div>
        <p className="mm" style={{ fontSize: 13, margin: '10px 0 0' }} role="status">
          Finding the scenes in your screenplay. The cost of the analysis is shown as soon as they are
          found.
        </p>
      </div>
    );
  }

  return (
    <AiCostApproval
      projectId={projectId}
      operationType="SCRIPT_ANALYSIS"
      scriptVersionId={scriptVersionId}
      testId="new-search-approval"
      onStarted={onStarted}
    />
  );
}

const MODES: readonly { mode: Mode; title: string; hint: string }[] = [
  { mode: 'UPLOAD', title: 'Upload a file', hint: 'Screenplay PDF, DOCX or TXT' },
  { mode: 'PASTE', title: 'Paste script or brief', hint: 'Treatment, creative brief, shot list' },
  { mode: 'MANUAL', title: 'Add requirements manually', hint: 'No script needed' },
];

function FilePicker({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  return (
    <div style={{ marginTop: 16 }}>
      <label
        style={{ display: 'inline-block', cursor: 'pointer' }}
        data-testid="search-upload-label"
      >
        <span className="ctl">{file === null ? 'Choose a file' : 'Replace'}</span>
        <input
          type="file"
          data-testid="search-upload"
          accept=".pdf,.docx,.txt,.fountain"
          onChange={(e) => {
            onPick(e.target.files?.[0] ?? null);
          }}
          style={{ display: 'none' }}
        />
      </label>

      {file === null ? null : (
        <div
          data-testid="search-file"
          style={{ marginTop: 12, padding: '14px 16px', borderLeft: '3px solid var(--ok, #2e7d32)', background: 'var(--color-surface)' }}
        >
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>{file.name}</p>
          <p className="mm" style={{ margin: '4px 0 0', fontSize: 12 }}>
            {/* SIZE ONLY. The design also showed "~118 pages detected", which
                nothing here can know before the file is parsed — a page count
                invented in the browser from a byte count would be wrong for
                every scanned PDF, which is exactly the case it would matter in. */}
            {(file.size / 1_000_000).toFixed(1)} MB
          </p>
        </div>
      )}
    </div>
  );
}

